import { useMemo, useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import type { InstitutionEntry } from '../lib/countries-with-institutions'

interface WizardInstitutionListProps {
  institutions: InstitutionEntry[]
  search: string
  onSelect: (institutionId: number) => void
  disabled?: boolean
  trailingIcon?: ReactNode
  emptyMessage?: string
  footer?: ReactNode
}

const DEFAULT_TRAILING_ICON = <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />

/**
 * Clickable institution card list (logo/letter avatar + name + trailing icon),
 * filtered by `search` (case-insensitive, by name). Shared by the Open Banking
 * bank picker and the non-Open-Banking institution picker so both routes look
 * and behave the same way.
 */
export function WizardInstitutionList({
  institutions,
  search,
  onSelect,
  disabled = false,
  trailingIcon = DEFAULT_TRAILING_ICON,
  emptyMessage = 'No institutions found.',
  footer,
}: WizardInstitutionListProps) {
  const [brokenLogoIds, setBrokenLogoIds] = useState<ReadonlySet<number>>(new Set())

  const searchLower = search.toLowerCase()
  const filteredInstitutions = useMemo(() => {
    if (!searchLower) return institutions
    return institutions.filter((institution) => institution.Name.toLowerCase().includes(searchLower))
  }, [institutions, searchLower])

  function handleLogoError(institutionId: number) {
    setBrokenLogoIds((previousIds) => new Set(previousIds).add(institutionId))
  }

  return (
    <div className="flex flex-col gap-1">
      {filteredInstitutions.map((institution) => (
        <button
          key={institution.Id}
          type="button"
          disabled={disabled}
          className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => onSelect(institution.Id)}
        >
          {institution.Logo && !brokenLogoIds.has(institution.Id) ? (
            <img
              src={institution.Logo}
              alt={institution.Name}
              className="h-8 w-8 object-contain rounded"
              onError={() => handleLogoError(institution.Id)}
            />
          ) : (
            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
              {institution.Name.charAt(0)}
            </div>
          )}
          <span className="flex-1 text-sm font-medium">{institution.Name}</span>
          {trailingIcon}
        </button>
      ))}
      {filteredInstitutions.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">{emptyMessage}</p>
      )}
      {footer}
    </div>
  )
}
